import React, { useState } from "react";
import sendIcon from "../../assets/send_icon.svg";
import { useForm } from "react-hook-form";
import { useCreateChatSession, useSendQuery } from "./api";
import { useNavigate } from "react-router";
import { BeatLoader } from "react-spinners";

type FormInput = {
  content: string;
};

export default function NewChat() {
  const { handleSubmit, register, reset, watch } = useForm<FormInput>();
  const watchedContent = watch("content", "");
  const [inputValue, setInputValue] = useState("");
  const [serverError, setServerError] = useState("");
  const navigate = useNavigate();
  const createChatSessionMutation = useCreateChatSession();
  const sendQueryMutation = useSendQuery();

  // Frontend validation function
  const validateQuery = (query: string) => {
    const errors: string[] = [];
    
    if (!query.trim()) {
      errors.push("Please enter your question");
    }
    
    if (query.length > 4000) {
      errors.push("Message too long (max 4000 characters)");
    }
    
    // Check for potentially harmful patterns
    const harmfulPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /javascript:/gi,
      /data:text\/html/gi
    ];
    
    if (harmfulPatterns.some(pattern => pattern.test(query))) {
      errors.push("Message contains invalid content");
    }
    
    return errors;
  };

  const onSubmit = (data: FormInput) => {
    if (!navigator.onLine) {
      setServerError("No internet connection. Please check your network.");
      return;
    }
    
    // Frontend validation
    const validationErrors = validateQuery(data.content);
    if (validationErrors.length > 0) {
      setServerError(validationErrors[0]);
      return;
    }
    createChatSessionMutation.mutate(undefined, {
      onSuccess: (res) => {
        sendQueryMutation.mutate(
          { sessionId: res.session_id, query: data.content },
          {
            onSuccess: () => {
              setInputValue("");
              navigate(`/chatbot/${res.session_id}`);
            },
            onError: (error: any) => {
              setServerError(
                error.response?.data?.message || "An error occurred"
              );
            },
          }
        );
      },
      onError: (error: any) => {
        setServerError(error.response?.data?.message || "An error occurred");
      },
    });
    setServerError("");
    reset();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(event.target.value);
  };
  return (
    <div className='bg-white h-[calc(100vh-5rem)] text-white mt-20 w-full'>
      <div className='flex flex-row'>
        <div className='justify-center items-center flex flex-col w-full space-y-16 md:space-y-20'>
          <p className='text-black text-md font-semibold mt-10 ml-4 w-full md:text-xl md:w-3/4'>
            Ask any question to explore our web-crawled data on cyber threats.
            Our AI is here to help!
          </p>
          <form
            className='items-center justify-center flex flex-col md:flex-row pb-2 w-full md:w-5/6'
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className='relative w-full md:flex-1'>
              <textarea
                className='w-full text-black border-solid border-2 border-[#D9D9D9] rounded-md outline-none px-2 py-2 resize-none min-h-[55px] max-h-[120px] overflow-y-auto'
                placeholder='Ask your question here...'
                rows={1}
                maxLength={350000}
                {...register("content", {
                  required: true,
                  onChange: (e) => {
                    e.target.style.height = "auto";
                    // grow up to 6rem (≈max-h-24)
                    e.target.style.height =
                      Math.min(e.target.scrollHeight, 96) + "px";
                  },
                })}
                onKeyDown={handleKeyDown}
              />
              <span className='absolute bottom-2 right-2 text-xs text-gray-500 select-none'>
                {watchedContent.length.toLocaleString()} /{" "}
                {(350000).toLocaleString()}
              </span>
            </div>
            <button
              type='submit'
              disabled={sendQueryMutation.isPending || !watchedContent.trim()}
              className='mt-2 md:mt-0 md:ml-4 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {sendQueryMutation.isPending ? (
                <BeatLoader
                  color='#0F4988'
                  loading={sendQueryMutation.isPending}
                  size={10}
                  aria-label='Loading Spinner'
                  data-testid='loader'
                />
              ) : (
                <img
                  src={sendIcon}
                  alt='Send Icon'
                  className='w-9 h-9 cursor-pointer'
                />
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
